import { t } from "@lingui/core/macro";
import {
  Container,
  Box,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useGetQuery } from "../../hooks/useGetQuery";
import ContentHeaderToolbar from "../contentHeaderToolbar";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import DataTable from "../dataTable";
import { AlertMessage, ApiTaxonomy } from "../../api/types";
import { taxonomiesTableColumns } from "./taxonomiesTableColumns";
import { useState } from "react";
import TaxonomyImportDialog, { ImportType } from "./taxonomyImportDialog";
import { ContainerWrapper } from "../containerWrapper";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import EmptyTableAlert from "../emptyTableAlert";
import InboxIcon from "@mui/icons-material/Inbox";
import { useNavigate } from "react-router-dom";
import AlertSnackbar from "../alertSnackbar";

const Taxonomies = () => {
  const [cacheBuster, setCacheBuster] = useState(crypto.randomUUID());
  const { data, error, loading } = useGetQuery<ApiTaxonomy[]>(
    `/api/taxonomies/all?cb=${cacheBuster}`
  );
  const navigate = useNavigate();
  const [currentImportType, setCurrentImportType] = useState<ImportType>();
  const [alertMessage, setAlertMessage] = useState<AlertMessage | undefined>(
    undefined
  );

  const [sortConfig, setSortConfig] = useState<{
    key: keyof ApiTaxonomy | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });

  const handleClosedImportDialog = (
    updateStatus: "none" | "success" | "error",
    reason: string | undefined
  ) => {
    setCurrentImportType(undefined);
    if (updateStatus === "success") {
      setCacheBuster(crypto.randomUUID());
    } else {
      if (updateStatus === "error" && reason) {
        setAlertMessage({
          message: reason || "",
          severity: updateStatus,
        });
      }
    }
  };

  const handleClickRow = (rowId: string) => {
    setAlertMessage({
      message: "",
      severity: undefined,
    });
    navigate(`/taxonomies/${rowId}`)
  };

  const [editableFilters, setEditableFilters] = useState([
    {
      column: "id",
      sortDescending: false,
    },
    {
      column: "createdAt",
      sortDescending: false,
    },
    {
      column: "packageName",
      sortDescending: false,
    },
    {
      column: "systemName",
      sortDescending: true,
    },
  ]);

  const setFilter: (id: string, value?: string | boolean | null, sortDescending?: boolean, enterPressed?: boolean) => void = (
    id,
    _value,
    sortDescending
  ) => {
    setSortConfig({
      key: id as keyof ApiTaxonomy,
      direction: sortDescending ? "desc" : "asc",
    });
    setEditableFilters((prevFilters) =>
      prevFilters.map((filter) => {
        if (filter.column === id) {
          return { ...filter, sortDescending: sortDescending ?? false };
        }
        return filter;
      })
    );
  };

  const sortedData =
    data?.sort((a, b) => {
      if (!sortConfig.key) return 0;
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    }) || [];

  return (
    <>
      <ContainerWrapper>
        <Container
          maxWidth={false}
          sx={{
            ml: 0,
            mb: 10,
            overflow: "auto",
          }}
          component="div"
        >
          <Paper
            elevation={0}
            sx={{
              minHeight: 650,
              overflow: "auto",
              display: "flex",
              justifyItems: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Container maxWidth={false}>
              <ContentHeaderToolbar
                titleIcon={<InsertDriveFileOutlinedIcon fontSize="large" />}
                title={t`Taxonomies`}
                actions={[
                  {
                    id: "scheme",
                    disabled: false,
                    title: t`Schemes`,
                    turnedOn: false,
                    icon: NoteAddOutlinedIcon,
                    clickAction: () => navigate("/permissionschemes"),
                  },
                  {
                    id: "importData",
                    disabled: false,
                    title: t`Import files`,
                    turnedOn: false,
                    icon: NoteAddOutlinedIcon,
                    clickAction: () => setCurrentImportType("data"),
                  },
                  {
                    id: "importTax",
                    disabled: false,
                    title: t`Import Taxonomy`,
                    turnedOn: false,
                    icon: NoteAddOutlinedIcon,
                    clickAction: () => setCurrentImportType("taxonomy"),
                  },
                ]}
                showIcons
                CreateButton={true}
                showReportButton
                createButtonWidth={220}
              />
            </Container>
            {!loading && error && (
              <EmptyTableAlert
                severity="info"
                subTitle={t`Could not retrieve taxonomies. Please try again later`}
                mainIcon={
                  <InboxIcon sx={{ fontSize: 75, mb: 2, fill: "#e6e6e6" }} />
                }
                showIcon={true}
              />
            )}
            {!loading && !error && data && (
              <DataTable<ApiTaxonomy>
                columns={taxonomiesTableColumns()}
                filters={editableFilters}
                setFilter={setFilter}
                data={sortedData || []}
                onClickRow={handleClickRow}
              />
            )}
            {loading && (
              <Container maxWidth={false} sx={{ minHeight: 550 }}>
                <Box
                  sx={{
                    minHeight: 550,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              </Container>
            )}
          </Paper>
        </Container>
      </ContainerWrapper>
      <TaxonomyImportDialog
        headerText={
          currentImportType === "data"
            ? t`Import common xbrl files`
            : currentImportType === "taxonomy"
              ? t`Import Taxonomy`
              : t`Import Taxonomy`
        }
        open={currentImportType !== undefined}
        onClose={handleClosedImportDialog}
        importType={currentImportType}
      />
      <AlertSnackbar
        alertMessage={alertMessage}
        updateAlertStatus={setAlertMessage}
      />
    </>
  );
};

export default Taxonomies;
